import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Navbar, Footer } from "../../components/Layout";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Plus, Trash2, Edit, X, Upload } from "lucide-react";

export default function PartnersAdmin() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);

  const load = () => api.get("/admin/partners").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Supprimer ce partenaire ?")) return;
    await api.delete(`/admin/partners/${id}`);
    toast.success("Supprimé"); load();
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Navbar />
      <Toaster richColors position="top-right" />
      <section className="max-w-5xl mx-auto px-6 md:px-8 py-12">
        <Link to="/admin" className="text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 mb-6 inline-flex items-center gap-2"><ArrowLeft className="w-3 h-3"/> Retour</Link>
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Partenaires</h1>
          <button onClick={() => setModal({})} data-testid="add-partner-btn" className="btn-brutal btn-brutal-solar px-5 py-3 font-bold uppercase tracking-wider text-sm flex items-center gap-2"><Plus className="w-4 h-4"/>Nouveau partenaire</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {items.length === 0 && <div className="col-span-full p-12 text-center text-slate-500 font-mono">Aucun partenaire.</div>}
          {items.map(p => (
            <div key={p.id} className="bg-white border border-slate-900/15 p-4 flex flex-col items-center" data-testid={`row-partner-${p.id}`}>
              <div className="h-20 w-full flex items-center justify-center mb-3 bg-slate-50">
                {p.logo_url ? <img src={p.logo_url} alt={p.name} className="max-h-16 object-contain"/> : <span className="font-display font-bold">{p.name}</span>}
              </div>
              <div className="font-display font-semibold text-sm text-center">{p.name}</div>
              <div className="text-xs text-slate-500 mt-1">{p.published ? "Publié" : "Masqué"} · {p.order}</div>
              <div className="flex gap-1 mt-3">
                <button onClick={() => setModal({ item: p })} className="p-2 border border-slate-900 hover:bg-slate-900 hover:text-white"><Edit className="w-3 h-3"/></button>
                <button onClick={() => remove(p.id)} className="p-2 border border-slate-900 hover:bg-red-600 hover:text-white hover:border-red-600"><Trash2 className="w-3 h-3"/></button>
              </div>
            </div>
          ))}
        </div>
      </section>
      <Footer />
      {modal && <PModal item={modal.item} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }}/>}
    </div>
  );
}

function PModal({ item, onClose, onSaved }) {
  const [f, setF] = useState(item || { name: "", logo_url: "", website: "", order: 100, published: true });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadLogo = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF(prev => ({ ...prev, logo_url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
    } catch { toast.error("Échec upload"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...f, order: parseInt(f.order)||100 };
      if (item) await api.put(`/admin/partners/${item.id}`, payload);
      else await api.post("/admin/partners", payload);
      toast.success("Enregistré"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white border border-slate-900 w-full max-w-lg p-8 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between"><h2 className="font-display font-bold text-xl">{item ? "Modifier" : "Nouveau partenaire"}</h2><button type="button" onClick={onClose}><X className="w-5 h-5"/></button></div>
        <input required value={f.name} onChange={set("name")} placeholder="Nom *" className="input"/>
        <div className="flex gap-2">
          <input value={f.logo_url} onChange={set("logo_url")} placeholder="Logo (URL)" className="input flex-1"/>
          <label className="btn-brutal bg-white border border-slate-900 px-3 py-2 text-xs font-bold uppercase tracking-wider cursor-pointer flex items-center gap-1"><Upload className="w-3 h-3"/>{uploading ? "..." : "Upload"}
            <input type="file" accept="image/*" onChange={e => e.target.files[0] && uploadLogo(e.target.files[0])} className="hidden"/>
          </label>
        </div>
        {f.logo_url && <img src={f.logo_url} alt="" className="h-20 object-contain self-start"/>}
        <input value={f.website} onChange={set("website")} placeholder="Site web (https://...)" className="input"/>
        <input type="number" value={f.order} onChange={set("order")} placeholder="Ordre" className="input"/>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.published} onChange={set("published")} className="w-4 h-4 accent-slate-900"/>Publié</label>
        <button disabled={saving} type="submit" className="btn-brutal btn-brutal-solar w-full px-6 py-3 font-bold uppercase tracking-wider text-sm">{saving ? "..." : "Enregistrer"}</button>
      </form>
    </div>
  );
}
